const finiteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clamp01 = (value, fallback = 0.5) => {
  const parsed = finiteNumber(value);
  if (parsed === null) return fallback;
  return Math.max(0, Math.min(1, parsed));
};

export function normalizeCollaborationCursor(cursor = {}) {
  if (!cursor || typeof cursor !== 'object') return null;

  const timelinePosition = finiteNumber(
    cursor.timelinePosition ?? cursor.timeline_position ?? cursor.beat
  );
  const surface = cursor.surface === 'timeline' || timelinePosition !== null
    ? 'timeline'
    : (cursor.surface || 'workspace');
  const xRatio = finiteNumber(cursor.xRatio ?? cursor.x_ratio);
  const yRatio = finiteNumber(cursor.yRatio ?? cursor.y_ratio);

  if (surface === 'timeline' && timelinePosition !== null) {
    const trackIndex = finiteNumber(cursor.trackIndex ?? cursor.track_index);
    return {
      surface: 'timeline',
      timelinePosition: Math.max(0, timelinePosition),
      trackId: cursor.trackId ?? cursor.track_id ?? null,
      trackIndex: trackIndex === null ? null : Math.max(0, Math.floor(trackIndex)),
      laneOffset: clamp01(cursor.laneOffset ?? cursor.lane_offset),
      xRatio: xRatio === null ? null : clamp01(xRatio),
      yRatio: yRatio === null ? null : clamp01(yRatio)
    };
  }

  if (xRatio !== null && yRatio !== null) {
    return {
      surface: 'workspace',
      xRatio: clamp01(xRatio),
      yRatio: clamp01(yRatio)
    };
  }

  const x = finiteNumber(cursor.x);
  const y = finiteNumber(cursor.y);
  if (x !== null && y !== null) {
    return { surface: 'legacy', x, y };
  }

  return null;
}

export function createWorkspaceCursorPosition({ clientX, clientY, bounds }) {
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) return null;
  return {
    surface: 'workspace',
    x_ratio: clamp01((clientX - bounds.left) / bounds.width),
    y_ratio: clamp01((clientY - bounds.top) / bounds.height)
  };
}

export function createTimelineCursorPosition({
  beat,
  trackId,
  trackIndex,
  laneOffset,
  clientX,
  clientY,
  bounds
}) {
  const timelinePosition = finiteNumber(beat);
  if (timelinePosition === null || !bounds || bounds.width <= 0 || bounds.height <= 0) return null;
  return {
    surface: 'timeline',
    timeline_position: Math.max(0, timelinePosition),
    track_id: trackId || null,
    track_index: Math.max(0, Math.floor(Number(trackIndex) || 0)),
    lane_offset: clamp01(laneOffset),
    x_ratio: clamp01((clientX - bounds.left) / bounds.width),
    y_ratio: clamp01((clientY - bounds.top) / bounds.height)
  };
}

export function timelineLaneTop({
  trackId,
  trackIndex,
  tracks = [],
  automationView = {},
  trackHeight,
  automationLaneHeight = 0
}) {
  const height = Math.max(1, Number(trackHeight) || 1);
  if (trackId === 'pattern-track' || trackIndex === 0) return 0;

  let resolvedIndex = tracks.findIndex((track) => String(track?.id) === String(trackId));
  if (resolvedIndex < 0 && Number.isInteger(trackIndex) && trackIndex > 0) {
    resolvedIndex = trackIndex - 1;
  }
  if (resolvedIndex < 0) return null;

  let top = height;
  for (let index = 0; index < resolvedIndex; index += 1) {
    top += height;
    if (automationView?.[tracks[index]?.id]) top += automationLaneHeight;
  }
  return top;
}

export function projectCursorToViewport({
  x,
  y,
  viewportWidth,
  viewportHeight,
  margin = 16,
  forceHorizontal = null,
  forceVertical = null
}) {
  const width = Math.max(1, Number(viewportWidth) || 1);
  const height = Math.max(1, Number(viewportHeight) || 1);
  const rawX = finiteNumber(x) ?? width / 2;
  const rawY = finiteNumber(y) ?? height / 2;
  const horizontal = forceHorizontal
    || (rawX < 0 ? 'left' : (rawX > width ? 'right' : null));
  const vertical = forceVertical
    || (rawY < 0 ? 'up' : (rawY > height ? 'down' : null));

  const projectedX = horizontal === 'left'
    ? margin
    : (horizontal === 'right'
      ? width - margin
      : Math.max(margin, Math.min(width - margin, rawX)));
  const projectedY = vertical === 'up'
    ? margin
    : (vertical === 'down'
      ? height - margin
      : Math.max(margin, Math.min(height - margin, rawY)));
  const direction = [vertical, horizontal].filter(Boolean).join('-') || null;

  return {
    x: projectedX,
    y: projectedY,
    offscreen: Boolean(direction),
    direction
  };
}

export function projectTimelineCursor({
  cursor,
  xLocal,
  laneTop,
  timelineBounds,
  scrollTop = 0,
  trackHeight,
  viewportWidth,
  viewportHeight,
  margin = 16
}) {
  if (!cursor || !timelineBounds || laneTop === null || laneTop === undefined) return null;
  const laneY = laneTop + (clamp01(cursor.laneOffset) * Math.max(1, Number(trackHeight) || 1));
  const localY = laneY - Math.max(0, Number(scrollTop) || 0);
  const forceHorizontal = xLocal < 0
    ? 'left'
    : (xLocal > timelineBounds.width ? 'right' : null);
  const forceVertical = localY < 0
    ? 'up'
    : (localY > timelineBounds.height ? 'down' : null);

  return projectCursorToViewport({
    x: forceHorizontal === 'left'
      ? -1
      : (forceHorizontal === 'right' ? viewportWidth + 1 : timelineBounds.left + xLocal),
    y: forceVertical === 'up'
      ? -1
      : (forceVertical === 'down' ? viewportHeight + 1 : timelineBounds.top + localY),
    viewportWidth,
    viewportHeight,
    margin,
    forceHorizontal,
    forceVertical
  });
}
